/**
 * Mutation hook for creating a promotion (Pricing Rule).
 *
 * Calls the centralized create_promotion endpoint which maps
 * the merchant-friendly promotion type to ERPNext Pricing Rule fields.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";
import type { PromotionTypeId } from "@/features/inventory/item-detail/components/experience-views/item/widgets/promotionTypes";

// ── Types ────────────────────────────────────────────────────

export interface CreatePromotionParams {
  item_code: string;
  promotion_type: PromotionTypeId;
  sale_name: string;
  discount_percentage?: number;
  rate?: number;
  min_qty?: number;
  customer_group?: string;
  valid_from?: string;
  valid_upto?: string;
  replace_rule_name?: string;
  apply_multiple_pricing_rules?: number;
}

export interface CreatePromotionResponse {
  name: string;
  title: string;
  promotion_type: PromotionTypeId;
}

// ── Hook ─────────────────────────────────────────────────────

export function useCreatePromotion(itemCode: string) {
  const queryClient = useQueryClient();

  return useMutation<CreatePromotionResponse, Error, CreatePromotionParams>({
    mutationFn: (params: CreatePromotionParams) =>
      frappeMethod<CreatePromotionResponse>(
        "zaviago_backend.api.pricing_promotion_actions.create_promotion",
        params
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["pricing-summary", itemCode],
      });
    },
  });
}
